import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { CRMLayout } from "@/components/CRMLayout";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Github, GitBranch, GitCommit, Server, ExternalLink, X } from "lucide-react";
import ProjectTabs from "../components/ProjectTabs";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function ProjectGitPage() {
  const { projectId } = useParams();
  const { token } = useAuth();
  const [git, setGit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commit, setCommit] = useState(null);
  const [commitLoading, setCommitLoading] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API_URL}/api/projects/${projectId}/git`, auth)
      .then(({ data }) => setGit(data))
      .catch(() => toast.error("Impossible de charger le dépôt"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [projectId]);

  const openCommit = async (sha) => {
    setCommitLoading(true);
    setCommit({ sha });
    try {
      const { data } = await axios.get(`${API_URL}/api/projects/${projectId}/git/commit/${sha}`, auth);
      setCommit(data);
    } catch (e) {
      toast.error("Diff indisponible");
      setCommit(null);
    } finally {
      setCommitLoading(false);
    }
  };

  const Tabs = () => (
    <ProjectTabs projectId={projectId} actif="git" />
  );

  if (loading) {
    return <CRMLayout><div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div></CRMLayout>;
  }

  const diffLine = (l) => {
    let cls = "text-slate-600";
    if (l.startsWith("+") && !l.startsWith("+++")) cls = "text-emerald-700 bg-emerald-50";
    else if (l.startsWith("-") && !l.startsWith("---")) cls = "text-red-700 bg-red-50";
    else if (l.startsWith("@@")) cls = "text-indigo-600 bg-indigo-50";
    else if (l.startsWith("diff ") || l.startsWith("index ")) cls = "text-slate-400";
    return cls;
  };

  return (
    <CRMLayout>
      <div className="p-6 lg:p-8 max-w-4xl">
        <Link to="/dashboard/projects" className="inline-flex items-center gap-2 text-slate-500 hover:text-indigo-600 mb-4">
          <ArrowLeft className="w-4 h-4" /> Retour aux projets
        </Link>
        <Tabs />

        {!git?.has_git ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">
            Aucun dépôt git rattaché à ce projet.<br />
            <span className="text-xs">Renseignez le dossier du projet pour activer le suivi Git.</span>
          </div>
        ) : (
          <>
            {/* Entête dépôt */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 mb-4 flex flex-wrap items-center gap-4">
              {git.github_url && (
                <a href={git.github_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-800 hover:text-indigo-600">
                  <Github className="w-4 h-4" /> {git.github_url.replace("https://github.com/", "")} <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              <span className="inline-flex items-center gap-1.5 text-sm text-slate-500"><GitBranch className="w-4 h-4" /> {git.branch}</span>
              {(git.ahead > 0 || git.behind > 0) && (
                <span className="text-xs text-amber-600">{git.ahead > 0 ? `${git.ahead} à pousser` : ""} {git.behind > 0 ? `${git.behind} en retard` : ""}</span>
              )}
              {git.deployed && <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600"><Server className="w-4 h-4" /> déployé</span>}
              {git.dirty && <span className="text-xs text-amber-600">modifications non commitées</span>}
            </div>

            {/* Timeline commits */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm divide-y divide-slate-100">
              {git.commits.map((c) => (
                <button key={c.hash} onClick={() => openCommit(c.hash)}
                  className="w-full flex items-start gap-3 px-4 py-3 hover:bg-slate-50 text-left">
                  <GitCommit className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-800 truncate">{c.subject}
                      {c.refs && <span className="ml-2 text-[10px] bg-indigo-50 text-indigo-600 rounded px-1.5 py-0.5">{c.refs.replace(/[()]/g, "")}</span>}
                    </div>
                    <div className="text-xs text-slate-400">
                      <span className="font-mono">{c.hash}</span> · {c.author} · {c.when}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {/* Modale diff */}
        {commit && (
          <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4" onClick={() => setCommit(null)}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-slate-200">
                <div className="font-mono text-sm text-slate-700">{commit.sha}</div>
                <button onClick={() => setCommit(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="overflow-auto p-4">
                {commitLoading ? (
                  <div className="flex justify-center py-10 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
                ) : (
                  <>
                    {commit.stat && <pre className="text-xs text-slate-500 mb-3 whitespace-pre-wrap font-mono">{commit.stat}</pre>}
                    <pre className="text-xs font-mono leading-relaxed overflow-x-auto">
                      {(commit.diff || "").split("\n").map((l, i) => (
                        <div key={i} className={`px-1 ${diffLine(l)}`}>{l || " "}</div>
                      ))}
                    </pre>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </CRMLayout>
  );
}
